import { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { focusWhenReady } from "../../focus";
import { Btn, Card, Field, Input, Textarea } from "../../components/Shared";
import { useApi } from "../../useApi";
import { LEGEND, SR_ONLY, SuccessNote } from "./parts";

// Management -> Sign-in page links (task 2026-10-22; backend routers/admin_login_links.py).
// GET /api/admin/login-links {links: [{title, url, description}]}; PUT {links} -> the GET body,
// 422 with one sentence for a list the server refuses (at most 20 links, a one-line title of
// 1-100 characters, an http(s) address with a host, a description of at most 300 characters).
// The list goes out in the PUBLIC GET /api/auth/config and LoginPage shows it to everyone.
// Rows are edited here and saved together; Save keeps the keyboard focus in the card (as
// TimezoneCard does), and so do Remove and the move buttons, whose button moves or goes away.

const MAX_LINKS = 20;
let nextKey = 0;
const withKey = l => ({ key: `l${nextKey++}`, title: l.title || "", url: l.url || "", description: l.description || "" });
const plain = rows => rows.map(({ title, url, description }) => ({ title: title.trim(), url: url.trim(), description: description.trim() }));

export default function LoginLinksCard() {
  const r = useApi("/admin/login-links");
  const [rows, setRows] = useState([]);
  const [stored, setStored] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    if (r.data) { setRows(r.data.links.map(withKey)); setStored(r.data.links); }
  }, [r.data]);

  // Focus a control of a row (or the Add button) once React has committed the change.
  const focusIn = (key, which) => focusWhenReady(() => {
    const form = formRef.current;
    if (!form) return null;
    const row = key && form.querySelector(`[data-link-key="${key}"]`);
    if (row) {
      const wanted = row.querySelector(`[data-action="${which}"] button`);
      if (wanted && !wanted.disabled) return wanted;
      return row.querySelector("[data-action] button:not([disabled])") || row.querySelector("input");
    }
    return form.querySelector('[data-action="add"] button') || form.querySelector("input");
  });

  const edit = (key, k) => e => {
    const value = e.target.value;
    setSaved(false);
    setRows(rs => rs.map(x => (x.key === key ? { ...x, [k]: value } : x)));
  };

  const add = () => {
    const row = withKey({});
    setSaved(false);
    setRows(rs => [...rs, row]);
    focusWhenReady(() => formRef.current && formRef.current.querySelector(`[data-link-key="${row.key}"] input`));
  };

  const remove = i => {
    const after = rows[i + 1] || rows[i - 1];
    setSaved(false);
    setRows(rs => rs.filter((_, j) => j !== i));
    focusIn(after && after.key, "remove");
  };

  const move = (i, by) => {
    const key = rows[i].key;
    setSaved(false);
    setRows(rs => {
      const out = [...rs];
      [out[i], out[i + by]] = [out[i + by], out[i]];
      return out;
    });
    focusIn(key, by < 0 ? "up" : "down");
  };

  const save = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    // Save is disabled while saving and stays disabled once the list equals the saved one, so
    // keyboard focus would fall to <body>; give it to the Add button (TimezoneCard, f84a309).
    try {
      const out = await api.put("/admin/login-links", { links: plain(rows) });
      setRows(out.links.map(withKey));
      setStored(out.links);
      setSaved(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
      focusIn(null);
    }
  };

  if (r.loading) return <Loading />;
  if (r.error) return <ErrorNote error={r.error} />;
  const unchanged = stored !== null && JSON.stringify(plain(rows)) === JSON.stringify(plain(stored.map(l => ({ ...l, description: l.description || "" }))));
  return (
    <Card>
      <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Sign-in page links</h2>
      <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 8px" }}>
        Links to manuals, tutorials and the like, shown under the sign-in box in the order below.
      </p>
      <p data-testid="links-public" style={{ fontSize: 13, color: "#92400e", margin: "0 0 14px" }}>
        These links are public: anyone who opens the sign-in page sees them, signed in or not. A link
        to an internal wiki still shows its address.
      </p>
      <ErrorNote error={error} />
      {saved && (
        <SuccessNote>
          Saved: the sign-in page shows {stored.length === 0 ? "no links" : `${stored.length} link(s)`}.
        </SuccessNote>
      )}
      <form ref={formRef} onSubmit={save} aria-label="Sign-in page links" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {rows.length === 0 && <p style={{ margin: 0, fontSize: 13, color: "#4b5563" }}>No links: the sign-in page shows none.</p>}
        {rows.map((row, i) => (
          <fieldset key={row.key} data-link-key={row.key}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 14px", margin: 0, minWidth: 0,
                             display: "flex", flexDirection: "column", gap: 10 }}>
            <legend style={LEGEND}>Link {i + 1}</legend>
            <Field label="Title" hint="One line, at most 100 characters.">
              <Input value={row.title} onChange={edit(row.key, "title")} maxLength={100} autoComplete="off" />
            </Field>
            <Field label="Address" hint="https:// or http:// only.">
              <Input type="url" value={row.url} onChange={edit(row.key, "url")} maxLength={2000}
                     autoComplete="off" spellCheck={false} placeholder="https://" />
            </Field>
            <Field label="Description (optional)" hint="Plain text, at most 300 characters.">
              <Textarea rows={2} value={row.description} onChange={edit(row.key, "description")} maxLength={300} />
            </Field>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span data-action="up">
                <Btn variant="secondary" disabled={busy || i === 0} onClick={() => move(i, -1)}>
                  Move up<span style={SR_ONLY}> link {i + 1}</span>
                </Btn>
              </span>
              <span data-action="down">
                <Btn variant="secondary" disabled={busy || i === rows.length - 1} onClick={() => move(i, 1)}>
                  Move down<span style={SR_ONLY}> link {i + 1}</span>
                </Btn>
              </span>
              <span data-action="remove">
                <Btn variant="secondary" disabled={busy} onClick={() => remove(i)}>
                  Remove<span style={SR_ONLY}> link {i + 1}</span>
                </Btn>
              </span>
            </div>
          </fieldset>
        ))}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <span data-action="add">
            <Btn variant="secondary" disabled={busy || rows.length >= MAX_LINKS} onClick={add}>Add link</Btn>
          </span>
          <Btn type="submit" disabled={busy || unchanged}>{busy ? "Saving…" : "Save"}</Btn>
        </div>
        {rows.length >= MAX_LINKS && (
          <p style={{ margin: 0, fontSize: 12, color: "#4b5563" }}>At most {MAX_LINKS} links.</p>
        )}
      </form>
    </Card>
  );
}
